import React, { useState } from 'react';
import { FiCheck } from 'react-icons/fi';
import scoreboard from '../../../assets/scoreboard.png';
import attackmap from '../../../assets/attackmap.png';
import Reveal from '../../fx/Reveal';

const platformTabs = [
    {
        id: 'ctf',
        title: 'Attack-Defense CTF',
        heading: 'Real-time attack-defense platform',
        description:
            'Battle-tested platform where participants defend their own infrastructure while attacking others in a live, multi-node environment. Designed to simulate realistic adversarial engagements.',
        features: [
            'Isolated virtual environments per team',
            'Automated service uptime & flag monitoring',
            'Real-time attack visualization & scoring',
            'Secure shell access with granular logging',
        ],
        image: attackmap,
    },
    {
        id: 'jeopardy',
        title: 'Jeopardy CTF',
        heading: 'Jeopardy-style CTF platform',
        description:
            'Scalable platform for challenge-based CTF competitions, ideal for universities, community events, and corporate upskilling with intuitive UX and real-time scoring.',
        features: [
            'Multi-category challenges (pwn, web, forensics, etc.)',
            'Auto-deployment of containerized challenges',
            'Live leaderboard with team analytics',
            'User-friendly challenge-solving interface',
        ],
        image: scoreboard,
    },
];

const OurPlatform = () => {
    const [activeTab, setActiveTab] = useState('ctf');
    const [fadeIn, setFadeIn] = useState(true);

    const handleTabSwitch = (id) => {
        if (id === activeTab) return;
        setFadeIn(false);
        setTimeout(() => {
            setActiveTab(id);
            setFadeIn(true);
        }, 120);
    };

    const active = platformTabs.find((t) => t.id === activeTab);

    return (
        <section className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
            <div className="absolute -top-32 right-0 w-[700px] h-[700px] rounded-full bg-red-700/10 blur-3xl pointer-events-none" />
            <div className="relative max-w-7xl mx-auto">
                <Reveal className="text-center mb-12">
                    <span className="pill tag inline-flex mb-4">PLATFORMS</span>
                    <h2 className="font-display text-4xl md:text-5xl font-bold">
                        Our <span className="text-gradient-red">platforms</span>
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto mt-3 text-lg">
                        State-of-the-art training environments for cybersecurity professionals.
                    </p>
                </Reveal>

                <div className="flex justify-center gap-3 mb-10">
                    {platformTabs.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => handleTabSwitch(t.id)}
                            className={`px-5 py-2.5 rounded-lg font-mono text-xs uppercase tracking-wider transition ${
                                activeTab === t.id
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                                    : 'bg-white/5 text-gray-400 border border-white/10 hover:border-red-500/30'
                            }`}
                        >
                            {t.title}
                        </button>
                    ))}
                </div>

                <div className={`transition-all duration-500 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div className="card p-8">
                            <h3 className="font-display text-2xl font-semibold mb-3">{active.heading}</h3>
                            <p className="text-gray-400 mb-6 leading-relaxed">{active.description}</p>
                            <ul className="space-y-3 text-gray-300 text-sm">
                                {active.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <FiCheck className="text-red-400 mt-0.5 flex-shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="relative">
                            <div className="absolute -inset-4 bg-red-500/10 blur-3xl rounded-3xl" />
                            <img
                                src={active.image}
                                alt={active.title}
                                className="relative rounded-xl border border-white/10 shadow-2xl w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OurPlatform;
